import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, FileText, MessageSquare, UserPlus, Building2, Trash2, Upload, AlertTriangle, Send, ChevronRight } from 'lucide-react';

const EVENT_ICONS = {
  'claim.created': { icon: FileText, color: 'text-emerald-500' },
  'claim.updated': { icon: FileText, color: 'text-blue-500' },
  'claim.deleted': { icon: Trash2, color: 'text-red-500' },
  'note.created': { icon: MessageSquare, color: 'text-amber-500' },
  'document.uploaded': { icon: Upload, color: 'text-violet-500' },
  'user.joined': { icon: UserPlus, color: 'text-emerald-500' },
  'company.created': { icon: Building2, color: 'text-blue-500' },
  'claims.overdue': { icon: AlertTriangle, color: 'text-red-500' },
  'police_report.pending': { icon: AlertTriangle, color: 'text-blue-500' },
  // Phase D: customer notification received from linked Provider
  'customer_notification.received': { icon: Send, color: 'text-teal-500' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const REPORT_RE = /[A-Z]{2,}\/[A-Z]+(?:-[A-Z]+)*-\d{8}\/\d+/g;

function NotifBody({ text }) {
  if (!text) return null;
  const reports = text.match(REPORT_RE);
  if (!reports || reports.length === 0) {
    return <p className="text-xs text-gray-500 mt-0.5">{text}</p>;
  }
  // Strip all report numbers from text to get the description
  let desc = text;
  for (const r of reports) desc = desc.replace(r, '');
  // Clean up leftover separators/whitespace (commas, colons with trailing space, etc.)
  desc = desc.replace(/[,:]\s*[,:]/g, ':').replace(/[,:]\s*$/, '').replace(/\s{2,}/g, ' ').trim();
  return (
    <div className="mt-0.5">
      {desc && <p className="text-xs text-gray-500">{desc}</p>}
      {reports.map((r, i) => (
        <p key={i} className="text-xs font-medium text-gray-600">{r}</p>
      ))}
    </div>
  );
}

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'provider'
  const [providerFilter, setProviderFilter] = useState(null); // provider_tenant_id or null
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);
  const navigate = useNavigate();

  // ── Derive Provider-update slice + distinct providers ──
  const providerUpdates = useMemo(
    () => notifications.filter((n) => n.event_type === 'customer_notification.received'),
    [notifications]
  );
  const distinctProviders = useMemo(() => {
    const seen = new Map();
    providerUpdates.forEach((n) => {
      const tid = n.metadata?.provider_tenant_id;
      const name = n.metadata?.provider_name;
      if (tid && !seen.has(tid)) seen.set(tid, name || 'Provider');
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [providerUpdates]);
  const providerUnread = useMemo(
    () => providerUpdates.filter((n) => !n.is_read).length,
    [providerUpdates]
  );

  // Reset Provider filter when leaving Provider tab so it doesn't stick on next open
  useEffect(() => {
    if (activeTab !== 'provider') setProviderFilter(null);
  }, [activeTab]);

  // Visible list = tab + chip filter
  const visibleNotifications = useMemo(() => {
    if (activeTab === 'provider') {
      return providerFilter
        ? providerUpdates.filter((n) => n.metadata?.provider_tenant_id === providerFilter)
        : providerUpdates;
    }
    return notifications;
  }, [activeTab, providerFilter, providerUpdates, notifications]);

  const markAllReadInScope = useCallback(async () => {
    if (activeTab === 'all') {
      await markAllRead();
      return;
    }
    // Provider tab: mark only the items currently visible
    for (const n of visibleNotifications) {
      if (!n.is_read) await markRead(n.notification_id);
    }
  }, [activeTab, markAllRead, markRead, visibleNotifications]);

  const startCloseTimer = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 1500);
  }, []);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);

  useEffect(() => { return () => cancelCloseTimer(); }, [cancelCloseTimer]);

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.notification_id);
    const meta = notif.metadata || {};
    const reportNo = meta.report_no;
    const companyName = meta.company_name;

    if (reportNo && companyName) {
      if (notif.event_type === 'note.created') {
        navigate('/companies', { state: { selectedCompany: companyName, openClaim: reportNo, openNotes: true } });
      } else if (notif.event_type === 'claim.created' || notif.event_type === 'claim.updated' || notif.event_type === 'customer_notification.received') {
        navigate('/companies', { state: { selectedCompany: companyName, openClaim: reportNo } });
      } else if (notif.link) {
        navigate(notif.link);
      }
    } else if (notif.link) {
      navigate(notif.link);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell-container">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full leading-none"
            data-testid="notification-unread-count"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[480px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col overflow-hidden"
          data-testid="notification-dropdown"
          onMouseLeave={startCloseTimer}
          onMouseEnter={cancelCloseTimer}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">Notifications</span>
            {visibleNotifications.some((n) => !n.is_read) && (
              <button
                onClick={markAllReadInScope}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark {activeTab === 'provider' ? 'these' : 'all'} read
              </button>
            )}
          </div>

          {/* Tab strip — only when at least one Provider update exists */}
          {providerUpdates.length > 0 && (
            <div className="flex border-b border-gray-100 px-2" data-testid="notification-tabs">
              {[
                { id: 'all',      label: 'All',              count: unreadCount },
                { id: 'provider', label: 'Provider updates', count: providerUnread },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-3 py-2 text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-teal-600 border-b-2 border-teal-500 -mb-px'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`notification-tab-${tab.id}`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold ${
                      activeTab === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Per-Provider filter chips — only when 2+ distinct Providers */}
          {activeTab === 'provider' && distinctProviders.length >= 2 && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-gray-100 bg-gray-50" data-testid="notification-provider-chips">
              <button
                onClick={() => setProviderFilter(null)}
                className={`px-2.5 py-0.5 text-[11px] rounded-full border transition-colors ${
                  providerFilter === null
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                }`}
                data-testid="notification-provider-chip-all"
              >
                All providers
              </button>
              {distinctProviders.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProviderFilter(p.id)}
                  className={`px-2.5 py-0.5 text-[11px] rounded-full border transition-colors ${
                    providerFilter === p.id
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                  }`}
                  data-testid={`notification-provider-chip-${p.id}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}

          {/* List */}
          <div className="overflow-y-auto flex-1" data-testid="notification-list">
            {visibleNotifications.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                {activeTab === 'provider' ? 'No updates from your Provider yet' : 'No notifications yet'}
              </div>
            ) : (
              visibleNotifications.map((notif) => {
                const evt = EVENT_ICONS[notif.event_type] || { icon: Bell, color: 'text-gray-500' };
                const Icon = evt.icon;
                const isProviderUpdate = notif.event_type === 'customer_notification.received';
                const providerName = notif.metadata?.provider_name;
                return (
                  <button
                    key={notif.notification_id}
                    onClick={() => handleClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className={`mt-0.5 shrink-0 ${evt.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {isProviderUpdate && providerName && (
                        <span
                          className="inline-block mb-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-teal-100 text-teal-700"
                          data-testid="notification-provider-pill"
                        >
                          {providerName}
                        </span>
                      )}
                      <p className={`text-sm leading-snug ${!notif.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notif.title}
                      </p>
                      <NotifBody text={notif.body} />
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[11px] text-gray-400">{timeAgo(notif.created_at)}</p>
                        {isProviderUpdate && notif.metadata?.report_no && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-teal-600" data-testid="notification-view-claim">
                            View claim <ChevronRight className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                    {!notif.is_read && (
                      <div className="mt-1.5 shrink-0">
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
